/**
 * Calendar labels for a loan schedule: the date of each payment, the payoff date, calendar-year grouping and chart ticks.
 * All maths is on local calendar dates (year, month, day numbers), never on Date objects parsed from strings, so the
 * result is the same in every time zone. The first payment date only LABELS the schedule; it never changes an amount.
 * Used by the site's Mortgage Calculator and, through scripts/sync-core.mjs, by the utility API and MCP tools.
 */
import { daysInMonth, parseLocalDate, type YMD } from "./dates";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Earliest and latest first-payment year the calculators accept. */
export const MIN_PAYMENT_YEAR = 1990;
export const MAX_PAYMENT_YEAR = 2100;

const pad = (n: number, w = 2) => String(n).padStart(w, "0");

/** YYYY-MM-DD. */
export const ymdToISO = (d: YMD): string => `${pad(d.y, 4)}-${pad(d.m)}-${pad(d.d)}`;

/** Parses YYYY-MM-DD as a calendar date. Null when it is not a real date or the year is outside the supported range. */
export function parsePaymentDate(value: string | null | undefined): YMD | null {
  if (!value) return null;
  const d = parseLocalDate(value.trim());
  return d && d.y >= MIN_PAYMENT_YEAR && d.y <= MAX_PAYMENT_YEAR ? d : null;
}

/** The 1st of the month after `today` (a typical first payment: one month after closing). */
export function defaultFirstPayment(today: YMD): YMD {
  return today.m === 12 ? { y: today.y + 1, m: 1, d: 1 } : { y: today.y, m: today.m + 1, d: 1 };
}

/**
 * Date of payment number `index` (0 = the first payment). Every date is worked out from the FIRST payment's day, so a payment
 * on the 31st lands on the last day of shorter months (Feb 28 or 29, Apr 30) and goes back to the 31st afterwards: no drift.
 */
export function paymentDate(first: YMD, index: number): YMD {
  const idx = first.y * 12 + (first.m - 1) + index;
  const y = Math.floor(idx / 12), m = (idx % 12) + 1;
  return { y, m, d: Math.min(first.d, daysInMonth(y, m)) };
}

/** Date of payment number `monthNo` counted from 1, the way the calculator numbers months. */
export const paymentDateForMonth = (first: YMD, monthNo: number): YMD => paymentDate(first, monthNo - 1);

/** "Mar 2056". */
export const formatMonthYear = (d: YMD): string => `${MONTHS[d.m - 1]} ${d.y}`;
/** "Mar 1, 2056". */
export const formatFullDate = (d: YMD): string => `${MONTHS[d.m - 1]} ${d.d}, ${d.y}`;

/** Whole months in a term given in years (the calculators run on whole months). */
export const termMonths = (years: number): number => Math.round(years * 12);

/**
 * "Nov 2026 to Oct 2027": the calendar months covered by plan months `from`..`to` (0-based, 0 = the month of the start date).
 * Used by projection tools (SIP, 401k, 529, college cost) to put real months on a year-by-year row.
 */
export function monthSpanLabel(start: YMD, from: number, to: number): string {
  const a = paymentDate(start, from), b = paymentDate(start, to);
  return a.y === b.y && a.m === b.m ? formatMonthYear(a) : `${formatMonthYear(a)} to ${formatMonthYear(b)}`;
}

/** Calendar months of plan year `yearNo` (counted from 1), cut short at `totalMonths` when the last year is partial. */
export function planYearLabel(start: YMD, yearNo: number, totalMonths: number = Infinity): string {
  return monthSpanLabel(start, (yearNo - 1) * 12, Math.min(yearNo * 12, totalMonths) - 1);
}

/** The date `months` months after `start` (an anniversary: a 31st start gives Feb 28 or 29, then back to the 31st). */
export const dateAfterMonths = (start: YMD, months: number): YMD => paymentDate(start, months);

export interface TermDates {
  /** Whole months in the term. */
  months: number;
  /** The start date, and the date of the last monthly step (month `months`, counted from 1). */
  first: YMD; last: YMD;
  /** The date the term ends: `months` months after the start. */
  end: YMD;
  /** False when `years` was not a whole number of months, so `end` is rounded to the nearest month. */
  exact: boolean;
}

/**
 * Real dates for a projection that runs `years` from `start`: first and last monthly step and the end date. Null when the term is
 * shorter than one month. Shared by the projection calculators (SIP, compound interest, FIRE, 529, 401k) and the utility API.
 */
export function termDates(start: YMD, years: number): TermDates | null {
  const months = termMonths(years);
  if (!Number.isFinite(years) || months < 1) return null;
  return { months, first: start, last: paymentDate(start, months - 1), end: paymentDate(start, months), exact: Math.abs(years * 12 - months) < 1e-9 };
}

export interface CalendarYearRow {
  /** Calendar year of the payments in this row. */
  year: number; payments: number; principal: number; interest: number; balance: number;
  firstDate: YMD; lastDate: YMD;
  /** Payment number (counted from 1) of the last payment in this year. */
  endMonth: number;
}

/**
 * Groups schedule rows by the calendar year of their payment date. The first and last years are often partial
 * (a first payment in October gives a 3-payment first year), which `payments` shows.
 */
export function calendarYearRows(rows: { month: number; principal: number; interest: number; extra: number; balance: number }[], first: YMD): CalendarYearRow[] {
  const out: CalendarYearRow[] = [];
  for (const r of rows) {
    const date = paymentDate(first, r.month - 1);
    let row = out[out.length - 1];
    if (!row || row.year !== date.y) { row = { year: date.y, payments: 0, principal: 0, interest: 0, balance: 0, firstDate: date, lastDate: date, endMonth: r.month }; out.push(row); }
    row.payments++; row.principal += r.principal + r.extra; row.interest += r.interest; row.balance = r.balance; row.lastDate = date; row.endMonth = r.month;
  }
  return out;
}

export interface DatedBalancePoint { /** Payments made so far (0 = before the first payment). */ index: number; balance: number; label: string }

/** Balance before the first payment and after the last payment of each calendar year, labelled with dates, for the chart. */
export function datedBalanceSeries(principal: number, rows: { month: number; principal: number; interest: number; extra: number; balance: number }[], first: YMD): DatedBalancePoint[] {
  const pts: DatedBalancePoint[] = [{ index: 0, balance: principal, label: `Before first payment (${formatFullDate(first)})` }];
  for (const y of calendarYearRows(rows, first)) {
    pts.push({ index: y.endMonth, balance: y.balance, label: `After ${formatFullDate(y.lastDate)}` });
  }
  return pts;
}

/** Year labels for the chart's time axis: each January that falls inside the schedule, thinned to at most `max`. */
export function yearTicks(first: YMD, totalPayments: number, max = 4): { year: number; index: number }[] {
  const last = paymentDate(first, Math.max(0, totalPayments - 1));
  const all: { year: number; index: number }[] = [];
  for (let y = first.y + 1; y <= last.y; y++) all.push({ year: y, index: (y - first.y) * 12 - (first.m - 1) });
  const keep = all.filter(t => t.index / Math.max(1, totalPayments) > 0.18 && t.index / Math.max(1, totalPayments) < 0.82);
  const step = [1, 2, 5, 10, 20].find(s => keep.filter(t => t.year % s === 0).length <= max) ?? 20;
  return keep.filter(t => t.year % step === 0);
}

/** Adds days to a calendar date (UTC arithmetic, so daylight saving cannot shift it). */
export function addDays(d: YMD, days: number): YMD {
  const t = new Date(Date.UTC(d.y, d.m - 1, d.d + days));
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
}
